import React from 'react';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  AppLaunchLocation,
  CurrentAppIntent,
  InstalledApp,
} from '@packages/systems/designer/apps/types';
import {weakMemo} from '@packages/utilities/memo';
import {
  isFormElement,
  isImageElement,
  isDOM,
} from '@packages/utilities/elementTypeUtils';
import {getInstalledAppsSelector} from '@packages/systems/designer/apps/utils';
import {useDesignerSelector} from '@packages/systems/designer/flux';

export type AppIntentItem = {
  app: InstalledApp;
  location: AppLaunchLocation;
  currentAppIntent: CurrentAppIntent;
};

export const getIntentForElement = weakMemo((element: ExpressionElement) => {
  if (isFormElement(element)) {
    return {
      type: 'form',
      intent: 'manage',
    };
  }

  if (isImageElement(element)) {
    return {
      type: 'image',
      intent: 'manage',
    };
  }

  if (isDOM(element)) {
    return {
      type: 'dom',
      intent: 'manage',
    };
  }

  return null;
});

const DISPLAY_LIMIT = 3;

const getAppIntentKey = (elementType: string, permission: string) => {
  return `${elementType}.${permission}`;
};

export const useAppIntents = (
  selectedElement: ExpressionElement,
  location: AppLaunchLocation
): Array<AppIntentItem> => {
  const installedApps = useDesignerSelector(getInstalledAppsSelector);
  /** This is flatten intermediary representation of the supported app intents
   * from every manifest of the installed apps. The key is the concatenation of
   * the elementType and the permission. The value is an array of installed apps
   * that support that intent.
   */
  const intentMap: Record<string, Array<InstalledApp>> = React.useMemo(
    () =>
      installedApps.reduce(
        (acc, app) => {
          const manifest =
            (app.launchDevByDefault ? app.devManifest : app.manifest) ||
            app.devManifest;

          if (!manifest || !manifest.appIntents) return acc;

          Object.entries(manifest.appIntents).forEach(
            ([elementType, permissions]) => {
              permissions.forEach((permission) => {
                const intentKey = getAppIntentKey(elementType, permission);
                if (intentKey in acc && acc[intentKey]) {
                  acc[intentKey].push(app);
                } else {
                  acc[intentKey] = [app];
                }
              });
            }
          );

          return acc;
        },
        {} as Record<string, Array<InstalledApp>>
      ),
    [installedApps]
  );

  if (!installedApps?.length) return [];

  const intentForElement = getIntentForElement(selectedElement);

  if (intentForElement) {
    const {type, intent} = intentForElement;

    const intentKey = getAppIntentKey(type, intent);

    if (intentKey in intentMap && intentMap[intentKey]) {
      const supportedApps = intentMap[intentKey];
      return supportedApps.slice(0, DISPLAY_LIMIT).map((app) => ({
        app,
        location,
        currentAppIntent: {
          [type]: intent,
        },
      }));
    }
  }

  return [];
};
